/**
 * Feb 2026 — Read-aloud button using OpenAI TTS (Onyx voice).
 *
 * The user prefers OpenAI's premium TTS voices over the browser's
 * native speech synthesizer (which sounds robotic on most devices).
 * We reuse the existing `POST /api/chat/tts` endpoint that already
 * powers the Chat OS voice, and fix the voice to "onyx" — a warm
 * deeper-male voice that matches the pastoral tone of the packets.
 *
 * Architecture:
 *   1. Extract text from a CSS-selector-scoped content root.
 *   2. Chunk it into ≤3500-character segments at sentence boundaries
 *      (OpenAI TTS caps each request at ~4096 chars).
 *   3. Pre-fetch the FIRST chunk's MP3 before showing "playing", so
 *      the user gets immediate audio.
 *   4. While chunk N plays, pre-fetch chunk N+1 in the background so
 *      playback is seamless (no audible gaps between chunks).
 *   5. A single Audio element is reused; we just swap `src` for each
 *      chunk. ObjectURLs are revoked as soon as a chunk finishes
 *      playing to release memory.
 *
 * State machine:
 *   idle      ▶ Read aloud
 *   loading   ⏳ Loading…    (first chunk fetch in flight, no Stop yet)
 *   playing   ⏸ Pause        (Stop button visible)
 *   paused    ▶ Resume       (Stop button visible)
 *
 * Cleanup:
 *   - Component unmount cancels in-flight fetches, pauses audio,
 *     revokes all blob URLs.
 *   - Stop button does the same and resets to idle.
 *
 * Privacy note:
 *   The TTS call sends the rendered page text to OpenAI. The user
 *   has already consented to OpenAI processing under the EULA (the
 *   text is the SAME content they already saw OpenAI generate one
 *   step earlier in the packet pipeline), so this is not a new
 *   privacy boundary.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VOICE = "onyx";
// OpenAI TTS hard limit is 4096 chars/request. We chunk smaller so
// each utterance has natural sentence boundaries and pause/resume
// stays responsive.
const MAX_CHUNK_CHARS = 3500;

/**
 * Extract readable text from a content root. Strips the read-aloud
 * controls themselves AND any element marked
 * `data-readaloud-skip="true"` (e.g. donation cards, footer chrome).
 */
function _collectText(rootSelector) {
  if (typeof document === "undefined") return "";
  const root = document.querySelector(rootSelector);
  if (!root) return "";
  const clone = root.cloneNode(true);
  clone
    .querySelectorAll(
      "[data-readaloud-control], [data-readaloud-skip='true'], script, style, button"
    )
    .forEach((el) => el.remove());
  const txt = clone.innerText || clone.textContent || "";
  return txt.replace(/\s+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Chunk text at sentence boundaries, keeping each chunk under
 * MAX_CHUNK_CHARS. If a single sentence exceeds the cap (rare —
 * pastoral prose rarely runs that long), it's hard-split at the
 * cap boundary.
 */
function _chunk(text) {
  const sentences = text
    .split(/(?<=[.!?])\s+(?=[A-Z“"'])/g)
    .map((s) => s.trim())
    .filter(Boolean);
  const chunks = [];
  let buf = "";
  for (const s of sentences) {
    if (s.length > MAX_CHUNK_CHARS) {
      if (buf) {
        chunks.push(buf);
        buf = "";
      }
      // Hard-split the single oversize sentence.
      for (let i = 0; i < s.length; i += MAX_CHUNK_CHARS) {
        chunks.push(s.slice(i, i + MAX_CHUNK_CHARS));
      }
      continue;
    }
    if (buf.length + s.length + 1 > MAX_CHUNK_CHARS && buf) {
      chunks.push(buf);
      buf = s;
    } else {
      buf = buf ? `${buf} ${s}` : s;
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

async function _fetchChunkMp3(text, signal) {
  const resp = await axios.post(
    `${API}/chat/tts`,
    { text, voice: VOICE },
    { responseType: "blob", signal, timeout: 120000 }
  );
  return URL.createObjectURL(resp.data);
}

export default function ReadAloudButton({
  contentSelector,
  testId = "read-aloud-btn",
  className = "",
}) {
  // State: idle | loading | playing | paused
  const [state, setState] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const audioRef = useRef(null);
  const chunksRef = useRef([]);          // string[] of remaining chunks
  const blobUrlsRef = useRef([]);        // ObjectURLs we must revoke on cleanup
  const abortRef = useRef(null);         // current in-flight fetch AbortController
  const prefetchRef = useRef(null);      // { idx, urlPromise } for chunk N+1
  const currentIdxRef = useRef(0);

  // Hard reset: cancel network, pause audio, revoke URLs, return to idle.
  const _hardStop = useCallback(() => {
    try {
      if (abortRef.current) abortRef.current.abort();
    } catch (e) { /* no-op */ }
    abortRef.current = null;
    prefetchRef.current = null;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
      } catch (e) { /* no-op */ }
    }
    blobUrlsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch (e) { /* no-op */ }
    });
    blobUrlsRef.current = [];
    chunksRef.current = [];
    currentIdxRef.current = 0;
    setState("idle");
  }, []);

  // Unmount cleanup.
  useEffect(() => {
    return () => { _hardStop(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Play chunk at index `idx`. Loads the audio src, awaits canplay,
  // starts playback, then triggers prefetch of the next chunk in
  // parallel. On `ended`, advances to the next chunk.
  const _playChunk = useCallback(async (idx) => {
    const chunks = chunksRef.current;
    if (idx >= chunks.length) {
      // All chunks played — reset to idle.
      _hardStop();
      return;
    }
    currentIdxRef.current = idx;
    let url;
    // If we prefetched this index, reuse the URL.
    if (prefetchRef.current && prefetchRef.current.idx === idx) {
      try {
        url = await prefetchRef.current.urlPromise;
      } catch (e) {
        // Prefetch failed — fall through and re-fetch synchronously.
        prefetchRef.current = null;
      }
    }
    if (!url) {
      abortRef.current = new AbortController();
      try {
        url = await _fetchChunkMp3(chunks[idx], abortRef.current.signal);
      } catch (e) {
        if (axios.isCancel(e) || e?.name === "CanceledError") return;
        setErrMsg("Read-aloud failed. Try again.");
        _hardStop();
        return;
      }
    }
    blobUrlsRef.current.push(url);

    // Start prefetching chunk idx+1 in parallel.
    if (idx + 1 < chunks.length) {
      const ctl = new AbortController();
      abortRef.current = ctl;
      prefetchRef.current = {
        idx: idx + 1,
        urlPromise: _fetchChunkMp3(chunks[idx + 1], ctl.signal).catch(() => null),
      };
    } else {
      prefetchRef.current = null;
    }

    const audio = audioRef.current;
    audio.src = url;
    audio.onended = () => { _playChunk(idx + 1); };
    audio.onerror = () => {
      setErrMsg("Audio playback error.");
      _hardStop();
    };
    try {
      await audio.play();
      setState("playing");
    } catch (e) {
      setErrMsg("Browser blocked playback.");
      _hardStop();
    }
  }, [_hardStop]);

  const _start = useCallback(async () => {
    setErrMsg("");
    const text = _collectText(contentSelector);
    if (!text) {
      setErrMsg("Nothing to read on this page yet.");
      return;
    }
    const chunks = _chunk(text);
    chunksRef.current = chunks;
    blobUrlsRef.current = [];
    currentIdxRef.current = 0;
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "auto";
    }
    setState("loading");
    _playChunk(0);
  }, [contentSelector, _playChunk]);

  const _pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setState("paused");
  }, []);

  const _resume = useCallback(async () => {
    if (audioRef.current) {
      try {
        await audioRef.current.play();
        setState("playing");
      } catch (e) {
        setErrMsg("Browser blocked playback.");
      }
    }
  }, []);

  let label;
  let handler;
  if (state === "loading") {
    label = "⏳ Loading…";
    handler = null;
  } else if (state === "playing") {
    label = "⏸ Pause";
    handler = _pause;
  } else if (state === "paused") {
    label = "▶ Resume";
    handler = _resume;
  } else {
    label = "▶ Read aloud";
    handler = _start;
  }

  return (
    <span
      className="inline-flex items-center gap-2"
      data-readaloud-control="true"
    >
      <button
        type="button"
        onClick={handler}
        disabled={state === "loading"}
        className={`rm-btn-ghost ${state === "loading" ? "opacity-60 cursor-wait" : ""} ${className}`}
        data-testid={testId}
        aria-label={label.replace(/[▶⏸⏳]\s*/, "")}
      >
        {label}
      </button>
      {(state === "playing" || state === "paused") && (
        <button
          type="button"
          onClick={_hardStop}
          className={`rm-btn-ghost ${className}`}
          data-testid={`${testId}-stop`}
          aria-label="Stop reading"
        >
          ■ Stop
        </button>
      )}
      {errMsg && (
        <span
          className="text-xs text-red-300/70"
          data-testid={`${testId}-error`}
          role="alert"
        >
          {errMsg}
        </span>
      )}
    </span>
  );
}
